import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCompany } from '../hooks/useCompany';
import { WmsOrdersService } from '../services/wmsOrdersService';
import { hasWms } from '../types/company';
import type { PicqerOrderFromApi } from '../services/picqerOrdersService';
import { PicqerOrdersList } from './PicqerOrders/PicqerOrdersList';

const { Title } = Typography;

export const PicqerOrdersPage: React.FC = () => {
  const { t } = useTranslation();
  const { selectedCompany: companyId, companyDetails, companyDetailsLoading } = useCompany();
  const isWmsConnected = hasWms(companyDetails ?? undefined);
  const wmsProvider = isWmsConnected && companyDetails?.wms_provider === 'channeldock' ? 'channeldock' : 'picqer';
  const wmsName = wmsProvider === 'channeldock' ? 'ChannelDock' : 'Picqer';
  const [wmsOrders, setWmsOrders] = useState<PicqerOrderFromApi[]>([]);
  const [wmsOrdersLoading, setWmsOrdersLoading] = useState(false);

  const loadWmsOrders = useCallback(async () => {
    if (!companyId || !isWmsConnected) return;
    setWmsOrdersLoading(true);
    try {
      const res = await WmsOrdersService.listOrders(companyId, wmsProvider);
      if (res.success && Array.isArray(res.data)) setWmsOrders(res.data);
      else setWmsOrders([]);
    } catch {
      setWmsOrders([]);
    } finally {
      setWmsOrdersLoading(false);
    }
  }, [companyId, isWmsConnected, wmsProvider]);

  useEffect(() => {
    loadWmsOrders();
  }, [loadWmsOrders]);

  if (!companyId) {
    return (
      <div style={{ padding: 24 }}>
        <Typography.Text type="secondary">{t('picqerOrders.selectCompany', { provider: wmsName })}</Typography.Text>
      </div>
    );
  }

  if (companyDetailsLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!isWmsConnected) {
    return (
      <div style={{ padding: 24 }}>
        <Typography.Text type="secondary">{t('picqerOrders.noWmsConnected', { provider: wmsName })}</Typography.Text>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginTop: 0, marginBottom: 16 }}>
        {t('picqerOrders.allWmsOrdersTitle', { provider: wmsName })}
      </Title>
      <PicqerOrdersList
        orders={wmsOrders}
        loading={wmsOrdersLoading}
        wmsProvider={wmsProvider}
        wmsName={wmsName}
      />
    </div>
  );
};
